import { Injectable } from '@angular/core';
import { Observable, Subscription, catchError, of, switchMap, tap, timer } from 'rxjs';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { OrderApi } from '../api/order.api';
import { OrderLockAPIResponse } from '../interface/order.interface';
import { OrderState } from '../state/order.state';

const LOCK_EXPIRED_MESSAGE = 'Your edit lock on this order has expired. The order is now open in Read-Only mode.';

export const ORDER_LOCK_DEFAULT_MESSAGE = 'Unable to open the order for editing. Please try after some time.';

@Injectable({
  providedIn: 'root',
})
export class OrderLockService {
  private _heartbeatSub?: Subscription;
  private _lockToken: string | null = null;
  private _orderId: string | null = null;
  lastError: string | null = null;
  lastErrorType: string | null = null;

  constructor(
    private orderApi: OrderApi,
    private orderState: OrderState,
    private _snackBarService: SnackBarService
  ) {}

  isLockHeld(orderId: string): boolean {
    return this._lockToken !== null && this._orderId === orderId;
  }

  /**
   * Acquire lock
   * Locks the order when the user opened the order for editing
   * Returns the lock response if lock is acquired for the order
   */
  acquireLock(orderId: string): Observable<OrderLockAPIResponse | null> {
    this._stopHeartbeat(false);
    this._orderId = orderId;
    this.lastError = null;
    this.lastErrorType = null;

    return this.orderApi.acquireLock(orderId).pipe(
      tap((response) => {
        if (response) {
          this._lockToken = response.lockToken;
          this._startHeartbeat(response.heartbeatIntervalSec);
          this._registerBeforeUnload();
        }
      }),
      catchError((error) => {
        this.lastError = error.error?.message ?? 'Unable to open the order. Please try after some time.';
        this.lastErrorType = error.error?.type ?? null;
        return of(null);
      })
    );
  }

  /**
   * Release lock
   * @param keepalive Use native fetch with keepalive to survive page unload (tab/browser close)
   */
  releaseLock(keepalive: true): void;
  releaseLock(keepalive?: false): Observable<boolean>;
  releaseLock(keepalive = false): Observable<boolean> | void {
    const orderId = this.orderState.orderDetail.id;
    const token = this._lockToken;

    if (!token) {
      this._stopHeartbeat(true);
      return keepalive ? undefined : of(false);
    }

    /** Keeps the token so a failed release can be retried. */
    this._stopHeartbeat(false);

    if (keepalive) {
      this.orderApi.releaseLock(orderId, token, true);
      this._stopHeartbeat(true);
      return undefined;
    }

    return (this.orderApi.releaseLock(orderId, token, false) as Observable<boolean>).pipe(
      tap((released) => {
        if (released) this._stopHeartbeat(true);
      })
    );
  }

  /**
   * Release lock while logging out
   */
  releaseLockOnLogout() {
    if (this.isLockHeld(this.orderState.orderDetail.id)) {
      this.releaseLock().subscribe();
    }
  }

  /** A retained token would make the next visit look editable, so ownership is dropped locally. */
  forgetLock(): void {
    this._stopHeartbeat(true);
    this._orderId = null;
    this.lastError = null;
    this.lastErrorType = null;
  }

  switchToReadOnlyMode(errorMessage: string, notify = true): void {
    this._stopHeartbeat(true);
    this.lastError = errorMessage;
    this.orderState.lockReadOnly = true;
    this.orderState.readOnlyMode = true;

    if (notify) {
      this._snackBarService.open('warning', errorMessage);
    }
  }

  /**
   * Start heartbeat using a timer to keep the lock alive
   */
  private _startHeartbeat(intervalSec: number): void {
    this._heartbeatSub?.unsubscribe();
    const intervalMs = intervalSec * 1000;

    this._heartbeatSub = timer(intervalMs)
      .pipe(
        switchMap(() =>
          this.orderApi.refreshLock(this.orderState.orderDetail.id).pipe(
            switchMap((response) => {
              if (response) {
                this._lockToken = response.lockToken;
                this._startHeartbeat(response.heartbeatIntervalSec);
                return of(true);
              }
              return this._reacquireOrSwitchToReadOnly();
            }),
            catchError(() => this._reacquireOrSwitchToReadOnly())
          )
        )
      )
      .subscribe();
  }

  private _reacquireOrSwitchToReadOnly(): Observable<boolean> {
    const orderId = this._orderId ?? this.orderState.orderDetail.id;

    return this.orderApi.acquireLock(orderId).pipe(
      switchMap((response) => {
        if (response) {
          this._lockToken = response.lockToken;
          this._startHeartbeat(response.heartbeatIntervalSec);
          return of(true);
        }

        this.switchToReadOnlyMode(LOCK_EXPIRED_MESSAGE);
        return of(false);
      }),
      catchError(() => {
        this.switchToReadOnlyMode(LOCK_EXPIRED_MESSAGE);
        return of(false);
      })
    );
  }

  private _beforeUnloadHandler = () => this._releaseOnUnload();

  private _releaseOnUnload(): void {
    if (this._lockToken && this._orderId) {
      this.orderApi.releaseLock(this._orderId, this._lockToken, true);
    }
  }

  private _registerBeforeUnload(): void {
    window.removeEventListener('beforeunload', this._beforeUnloadHandler);
    window.addEventListener('beforeunload', this._beforeUnloadHandler);
  }

  private _removeBeforeUnload(): void {
    window.removeEventListener('beforeunload', this._beforeUnloadHandler);
  }

  /**
   * Stop refresh subscription and optionally clear the token
   */
  private _stopHeartbeat(clearToken: boolean): void {
    this._heartbeatSub?.unsubscribe();
    this._heartbeatSub = undefined;
    if (clearToken) {
      this._lockToken = null;
      this._removeBeforeUnload();
    }
  }

  get lockToken(): string | null {
    return this._lockToken;
  }
}
